/**
 * Event WebSocket Connection Manager
 * Handles WebSocket connection lifecycle for WebPush events
 */

import type { EventConnectionState } from './types';

/**
 * Connection configuration interface
 */
export interface EventConnectionConfig {
  url: string;
  maxReconnectAttempts: number;
  reconnectDelay: number;
  heartbeatInterval?: number;
}

/**
 * Connection event callbacks
 */
export interface EventConnectionCallbacks {
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Event) => void;
  onMessage?: (event: MessageEvent) => void;
  onStateChange?: (state: EventConnectionState) => void;
}

/**
 * WebSocket dependency interface for testing
 */
export interface EventWebSocketDependency {
  new(url: string): WebSocket;
}

/**
 * Event Connection Manager class
 * Manages WebSocket connection for WebPush events with automatic reconnection
 */
export class EventConnectionManager {
  private config: EventConnectionConfig;
  private callbacks: EventConnectionCallbacks = {};
  private WebSocketClass: EventWebSocketDependency;
  
  // Connection state
  private ws: WebSocket | null = null;
  private currentState: EventConnectionState = 'idle';
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts: number = 0;
  private isConnecting: boolean = false;

  /**
   * Create event connection manager
   * @param config - Connection configuration
   * @param WebSocketClass - WebSocket constructor for dependency injection
   * @param callbacks - Event callbacks
   */
  constructor(
    config: EventConnectionConfig, 
    WebSocketClass: EventWebSocketDependency = WebSocket,
    callbacks: EventConnectionCallbacks = {}
  ) {
    this.config = config;
    this.WebSocketClass = WebSocketClass;
    this.callbacks = callbacks;
  }

  /**
   * Connect to Event WebSocket server
   */
  connect(): void {
    if (this.isConnecting || this.currentState === 'connected') {
      return;
    }

    try {
      this.isConnecting = true;
      this.setState('connecting');
      
      this.ws = new this.WebSocketClass(this.config.url);
      this.setupWebSocketHandlers();
      
    } catch (error) {
      this.isConnecting = false;
      this.setState('failed');
      this.callbacks.onError?.(error as Event);
    }
  }

  /**
   * Disconnect from Event WebSocket server
   */
  disconnect(): void {
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();
    this.reconnectAttempts = this.config.maxReconnectAttempts; // Disable reconnection
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.setState('disconnected');
    this.isConnecting = false;
  }

  /**
   * Get current connection state
   */
  getState(): EventConnectionState {
    return this.currentState;
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.currentState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Update connection configuration
   */
  updateConfig(newConfig: Partial<EventConnectionConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Update callbacks
   */
  setCallbacks(callbacks: EventConnectionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Destroy connection manager
   */
  destroy(): void {
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();
    this.disconnect();
    this.callbacks = {};
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0; // Reset on successful connection
      this.setState('connected');
      this.startHeartbeat();
      this.callbacks.onOpen?.();
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      this.clearHeartbeatTimer();
      
      if (event.code === 1006) {
        this.setState('reconnecting');
        this.scheduleReconnect();
      } else {
        this.setState('disconnected');
      }
      
      this.callbacks.onClose?.(event);
    };

    this.ws.onerror = (error) => {
      this.isConnecting = false;
      this.setState('failed');
      this.callbacks.onError?.(error);
    };

    this.ws.onmessage = (event) => {
      this.callbacks.onMessage?.(event);
    };
  }

  /**
   * Schedule automatic reconnection
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.setState('failed');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.setState('reconnecting');
    
    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );


    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Start heartbeat monitoring
   */
  private startHeartbeat(): void {
    if (!this.config.heartbeatInterval) return;
    
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // AWS API Gateway idle timeout 방지를 위한 ping 메시지 전송
        try {
          const pingMessage = JSON.stringify({ type: 'ping' });
          this.ws.send(pingMessage);
        } catch (error) {
          // Ping 전송 실패 시에도 연결은 유지 (서버에서 처리할 수 있음)
        }
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Clear heartbeat timer
   */
  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Set connection state and notify callbacks
   */
  private setState(state: EventConnectionState): void {
    if (this.currentState !== state) {
      const previousState = this.currentState;
      this.currentState = state;
      this.callbacks.onStateChange?.(state);
    }
  }
}